import React, { useEffect, useRef, useState } from 'react';
import { useGameController } from '../hooks';
import { BlinkoGame } from '../api';
import { useAtomValue, useSetAtom } from 'jotai';
import { 
  atomBoardReady, 
  atomIsReplayMode,
  atomGameState,
  atomSessionHits,
  atomIsGamePreparing,
  atomTotalWinEth,
  atomBallsRemaining
} from '../states/game.atoms';
import { formatEther } from 'viem';
import { useAssets } from './AssetHandler';
import { GameActionsProvider } from '../contexts/GameActionsContext';
import { BackgroundLayer } from './BackgroundLayer';
import { BoardLayer } from './BoardLayer';
import { motion } from 'framer-motion';

interface VerifyGameRunnerProps {
  game: BlinkoGame;
  onComplete: (success: boolean, message?: string) => void;
  onCompleteWithStats?: (success: boolean, winAmount: bigint, message?: string) => void;
  physicsSteps?: number;
}

export const VerifyGameRunner: React.FC<VerifyGameRunnerProps> = ({ game, onComplete, onCompleteWithStats, physicsSteps = 10 }) => {
  const { canvasRef, canvasDimensions, actions, verifyGame } = useGameController();
  // Note: 'physicsSteps' isn't natively supported by useGameController yet based on current file content, 
  // but the user's snippet implied it. I will ignore it for now or assume internal handling if I was modifying the engine.
  // For now, I will pass it to useGameController if I modify useGameController to accept it, 
  // or just ignore it if the engine doesn't support it dynamically yet.
  // Looking at useGameController, it doesn't take props currently.
  // I will stick to what the user provided in the request as much as possible, 
  // but `useGameController({ physicsSteps })` needs support in the hook.
  // I'll add support for it in the hook update step.

  // Atoms
  const boardReady = useAtomValue(atomBoardReady);
  const isGamePreparing = useAtomValue(atomIsGamePreparing);
  const gameState = useAtomValue(atomGameState);
  const setIsReplayMode = useSetAtom(atomIsReplayMode);
  const sessionHits = useAtomValue(atomSessionHits);
  const totalWinEth = useAtomValue(atomTotalWinEth);
  const ballsRemaining = useAtomValue(atomBallsRemaining);

  const { markAssetsReady } = useAssets();
  
  const [started, setStarted] = useState(false);
  const [simulationFinished, setSimulationFinished] = useState(false);
  
  // Safety timeout
  useEffect(() => {
    const t = setTimeout(() => {
      if (!simulationFinished) {
         onComplete(false, "Timeout waiting for simulation");
      }
    }, 30000); // 30s max per game
    return () => clearTimeout(t);
  }, [simulationFinished, onComplete]);

  useEffect(() => {
    setIsReplayMode(true);
  }, [setIsReplayMode]);

  // Verification Logic
  useEffect(() => {
    if (simulationFinished || !started) return;

    if (gameState === 'maxlevel' || gameState === 'gameover') {
       // Wait for all balls to be processed
       if (sessionHits.length < game.numDrops) return;

       // Server Win
       const serverWin = BigInt(game.gameState?.totalWin || '0');
       const isMatch = totalWinEth === serverWin;
       
       console.log("Verify Result:", isMatch, { clientWin: formatEther(totalWinEth), serverWin: formatEther(serverWin) });
       
       setSimulationFinished(true);
       const msg = isMatch ? undefined : `Mismatch: Client=${formatEther(totalWinEth)} Server=${formatEther(serverWin)}`;
       onComplete(isMatch, msg);
       onCompleteWithStats?.(isMatch, totalWinEth, msg);
    }
  }, [gameState, sessionHits, totalWinEth, simulationFinished, started, onComplete, onCompleteWithStats, game]);

  // Auto-Start Logic (Initial)
  const loadedRef = useRef(false);

  // Auto-Start Logic (Initial)
  // const loadedRef = useRef(false); // Removed duplicate

  useEffect(() => {
    // Only auto-start if explicitly enabled (passed as prop - though for this user request, we want it disabled by default or controlled)
    // Actually, the user said "clicking item shouldn't start it".
    // So distinct from "autoStart logic", we just won't call setStarted(true) here unless some condition is met.
    // I'll effectively disable this block's ability to `setStarted(true)` and rely on the manual handleManualStart function I added.
    // But I should keep it for cases where we DO want auto-start (like maybe a "Run All" feature later).
    // For now, I will comment it out or put it behind a flag.
  }, []); 

  // We are replacing the old auto-start effect with... nothing, or a conditional one.
  // The existing code was:
  // useEffect(() => {
  //   if (!loadedRef.current || started || !actions || isGamePreparing) return;
  //   if (gameState === 'waiting') {
  //     console.log("Verify: Starting Game...");
  //     setStarted(true);
  //     actions.startGame();
  //   }
  // }, [actions, started, gameState, isGamePreparing, boardReady]);
  
  // I will simply remove this effect's body since I added the manual button.

  // Auto-Drop Logic (Any Level / Stage)
  useEffect(() => {
    if (gameState === 'playing' && ballsRemaining > 0 && actions) {
       console.log("Verify: Dropping Balls", { ballsRemaining });
       // Small delay to ensure state / UI is ready
       const timer = setTimeout(() => {
         actions.dropMultipleBalls(ballsRemaining);
       }, 500);
       return () => clearTimeout(timer);
    }
  }, [gameState, ballsRemaining, actions]);

  // Auto-Play Logic (Bonus Stage)
  const bonusTriggeredRef = useRef(false);
  useEffect(() => {
    if (gameState === 'bonus' && !bonusTriggeredRef.current && actions) {
      console.log("Verify: Ready to auto-play (Bonus)");
      bonusTriggeredRef.current = true;
      // Allow a small delay for transition animation
      setTimeout(() => {
        actions.dropMultipleBalls(10); // Bonus usually has fixed drops
      }, 1000);
    }
  }, [gameState, actions]);

  // Initialize Game
  useEffect(() => {
    if (!game || loadedRef.current || !boardReady) return;
    
    markAssetsReady();
    verifyGame(game.gameId);
    loadedRef.current = true;
    bonusTriggeredRef.current = false; // Reset for new game
  }, [game, boardReady, verifyGame, markAssetsReady]);

  // Manual Start Handler
  const handleManualStart = () => {
    if (gameState === 'waiting' && actions) {
        setStarted(true);
        actions.startGame();
    }
  };

  return (
    <GameActionsProvider actions={actions}>
       <div className="relative w-full h-full bg-[#13151b] overflow-hidden">
        <BackgroundLayer />
        <motion.div 
          animate={{ opacity: 1 }}>
          <BoardLayer />
          <canvas
            ref={canvasRef}
            width={canvasDimensions.width}
            height={canvasDimensions.height}
            className="absolute inset-0 z-20"
          />
        </motion.div>
        
        {/* Overlay for Manual Start */}
        {!started && !simulationFinished && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                <button 
                    onClick={handleManualStart}
                    disabled={gameState !== 'waiting'}
                    className="px-8 py-3 bg-white text-black text-sm font-bold uppercase tracking-widest rounded-full hover:bg-neutral-200 transition-all shadow-[0_0_20px_rgba(255,255,255,0.3)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                    Start Verification
                </button>
            </div>
        )}
       </div>
    </GameActionsProvider>
  );
};
